import {
  createUserWithEmailAndPassword,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { defineStore } from "pinia";
import { markRaw } from "vue";
import { auth, db } from "../firebase"; // Assuming firebase.js exports auth
import { handleApiError } from "../utils/errorHandler";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    userProfile: null, // Firestore user document data
    profileUnsubscribe: null, // Unsubscribe function for profile listener
    loading: false,
    error: null,
    authInitialized: false,
    redirectAfterAuth: null,
  }),

  getters: {
    // Consider a user "authenticated" only if there's a user and they are not an anonymous/guest user.
    isAuthenticated: (state) => !!state.user && !state.user?.isAnonymous,
    // Explicit helper to check if the current user is an anonymous/guest user (Firebase sets `isAnonymous`).
    isAnonymous: (state) => !!state.user && !!state.user.isAnonymous,
    isLoading: (state) => state.loading,
    authError: (state) => state.error,
    isAuthInitialized: (state) => state.authInitialized,
    userId: (state) => state.user?.uid || null,
    subscriptionPlan: (state) => state.userProfile?.planId || 'free',
    subscriptionStatus: (state) => state.userProfile?.subscriptionStatus || 'inactive',

    // Subscription plan helpers
    isFreePlan: (state) => {
      const plan = state.userProfile?.planId || 'free'
      return plan === 'free' || plan === 'free_tier' || !plan
    },
    isProPlan: (state) => {
      const plan = state.userProfile?.planId
      const proPlanId = process.env.VUE_APP_PAYPAL_PRO_PLAN_ID || 'P-3RX065706M3469439L4Z4ZWA'
      return plan === 'pro' || plan === proPlanId
    },
    isBusinessPlan: (state) => {
      const plan = state.userProfile?.planId
      return plan === 'business'
    },
    canUpgrade: (state) => {
      const plan = state.userProfile?.planId
      return plan !== 'business'
    },
  },

  actions: {
    // Set return URL for redirect-based authentication
    setReturnUrl(url) {
      if (url && url !== "/") {
        localStorage.setItem("auth_returnUrl", url);
      }
    },

    // Email validation utility
    validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const commonDomains = [
        "gmail.com",
        "yahoo.com",
        "hotmail.com",
        "outlook.com",
        "live.com",
        "icloud.com",
        "protonmail.com",
      ];

      if (!emailRegex.test(email)) {
        return {
          isValid: false,
          message: "Please enter a valid email address.",
        };
      }

      const domain = email.split("@")[1]?.toLowerCase();
      if (domain && !commonDomains.includes(domain) && !domain.includes(".")) {
        return { isValid: false, message: "Please use a real email address." };
      }

      return { isValid: true };
    },

    // Password validation utility
    validatePassword(password) {
      if (password.length < 8) {
        return { isValid: false, message: "Password must be at least 8 characters long." };
      }

      if (!/(?=.*[a-z])/.test(password)) {
        return { isValid: false, message: "Password must contain lowercase letters." };
      }

      if (!/(?=.*[A-Z])/.test(password)) {
        return { isValid: false, message: "Password must contain uppercase letters." };
      }

      if (!/(?=.*\d)/.test(password)) {
        return { isValid: false, message: "Password must contain numbers." };
      }

      return { isValid: true };
    },

    async initAuth() {
      return new Promise((resolve) => {
        /*const unsubscribe = */ onAuthStateChanged(
        auth,
        async (currentUser) => {
          // Commented out unused unsubscribe

          // Clean up previous profile listener if exists
          if (this.profileUnsubscribe) {
            this.profileUnsubscribe();
            this.profileUnsubscribe = null;
          }

          this.user = currentUser ? markRaw(currentUser) : null;

          // If user is logged in, subscribe to their profile
          if (currentUser) {
            try {
              this.profileUnsubscribe = onSnapshot(
                doc(db, "users", currentUser.uid),
                (doc) => {
                  if (doc.exists()) {
                    this.userProfile = doc.data();
                  } else {
                    this.userProfile = null;
                  }
                },
                (error) => {
                  console.error("Error listening to user profile:", error);
                }
              );
            } catch (err) {
              console.error("Failed to setup profile listener:", err);
            }
          } else {
            this.userProfile = null;
          }

          if (!this.authInitialized) {
            this.authInitialized = true;
          }

          // Check for redirect result after Google Sign-In
          try {
            const result = await getRedirectResult(auth);
            if (result && result.user) {
              console.log(
                "Google Sign-In redirect successful:",
                result.user.email
              );

              // Check if there's a saved returnUrl from localStorage
              const savedReturnUrl = localStorage.getItem("auth_returnUrl");
              if (savedReturnUrl) {
                localStorage.removeItem("auth_returnUrl");
                // Use Vue Router to navigate (this will be handled by the component)
                this.redirectAfterAuth = savedReturnUrl;
              }
            }
          } catch (error) {
            console.error("Error getting redirect result:", error);
            // Don't set this.error here as it might interfere with normal auth flow
          }

          resolve(currentUser);
          // It's common to unsubscribe after the first state is known
          // or keep it running if you want real-time updates throughout the app session.
          // For simplicity here, we resolve and let it run.
          // If you need to unsubscribe, call unsubscribe().
        },
        (error) => {
          console.error("Auth state change error:", error);
          this.error = "Error initializing authentication state.";
          if (!this.authInitialized) {
            this.authInitialized = true;
          }
          resolve(null);
        }
      );
      });
    },

    async signInWithGoogle() {
      this.loading = true;
      this.error = null;
      const provider = new GoogleAuthProvider();
      provider.addScope("profile");
      provider.addScope("email");
      // Optionally, always prompt for account selection
      provider.setCustomParameters({
        prompt: "select_account",
      });

      try {
        // Try popup first, fallback to redirect if blocked
        const result = await signInWithPopup(auth, provider);
        // onAuthStateChanged will handle setting this.user
        // You can access result.user here if needed immediately
        console.log("Google Sign-In successful", result.user.email);
        return { success: true, user: result.user };
      } catch (error) {
        console.error("Google Sign-In Error:", error);

        // Handle specific popup errors by falling back to redirect
        if (
          error.code === "auth/popup-blocked" ||
          error.code === "auth/popup-closed-by-user" ||
          error.message.includes("popup") ||
          error.message.includes("cross-origin")
        ) {
          console.log("Popup failed, attempting redirect...");
          try {
            // Save current returnUrl from the component before redirect
            // This will be set by the component calling this method
            // Use redirect as fallback
            await signInWithRedirect(auth, provider);
            // Note: redirect will reload the page, so this return won't execute
            return { success: true, redirect: true };
          } catch (redirectError) {
            console.error("Redirect Sign-In Error:", redirectError);
            this.error =
              "Authentication failed. Please try again or use email/password login.";
            return { success: false, error: this.error };
          }
        } else {
          // Handle other authentication errors
          if (error.code === "auth/unauthorized-domain") {
            this.error =
              "This domain is not authorized. Please contact support.";
          } else if (error.code === "auth/network-request-failed") {
            this.error =
              "Network error. Please check your connection and try again.";
          } else {
            this.error = "Failed to sign in with Google. Please try again.";
          }
          return { success: false, error: this.error };
        }
      } finally {
        this.loading = false;
      }
    },

    async signOutUser() {
      this.loading = true;
      this.error = null;
      try {
        await signOut(auth);
        // onAuthStateChanged will handle setting this.user to null
        console.log("User signed out successfully.");
      } catch (error) {
        console.error("Sign Out Error:", error);
        this.error = "Failed to sign out. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    // Email/Password Authentication Methods
    async createUserWithEmail(email, password, displayName = "") {
      this.loading = true;
      this.error = null;

      try {
        // Validate email
        const emailValidation = this.validateEmail(email);
        if (!emailValidation.isValid) {
          this.error = emailValidation.message;
          return { success: false, error: this.error };
        }

        // Validate password
        const passwordValidation = this.validatePassword(password);
        if (!passwordValidation.isValid) {
          this.error = passwordValidation.message;
          return { success: false, error: this.error };
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

        // Update display name if provided
        if (displayName.trim()) {
          await updateProfile(userCredential.user, {
            displayName: displayName.trim(),
          });
        }

        // Send verification email immediately after account creation
        try {
          // Prefer a reasonable continue URL so the verification link returns to the app
          const actionCodeSettings = {
            url: window?.location?.origin + "/verify-email",
            // Let the email link open in browser by default. Adjust if you handle links in-app.
            handleCodeInApp: false,
          };

          await sendEmailVerification(userCredential.user, actionCodeSettings);
          console.log(
            "User created and verification email sent:",
            userCredential.user.email
          );
        } catch (sendErr) {
          // Surface a clearer error message while still returning created user information
          const formatted = handleApiError(sendErr);
          console.error("Failed to send verification email after account creation:", sendErr);
          this.error = formatted.message || "Failed to send verification email.";
          return { success: false, user: userCredential.user, error: this.error, code: sendErr.code };
        }

        return { success: true, user: userCredential.user, message: "Account created! Check your email for verification." };
      } catch (error) {
        console.error("Error creating user:", error);
        // Handle specific error codes with Kenyan context
        if (error.code === "auth/email-already-in-use") {
          this.error = "This email address is already in use. Try signing in or use a different email.";
        } else if (error.code === "auth/invalid-email") {
          this.error = "Please enter a valid email address.";
        } else if (error.code === "auth/weak-password") {
          this.error = "The password is too weak. Please choose a stronger password.";
        } else if (error.code === "auth/operation-not-allowed") {
          this.error = "This sign-up method is not enabled.";
        } else {
          this.error = "Failed to create account. Please try again.";
        }
        return { success: false, error: this.error };
      } finally {
        this.loading = false;
      }
    },

    async signInWithEmail(email, password) {
      this.loading = true;
      this.error = null;

      try {
        // Basic email validation
        const emailValidation = this.validateEmail(email);
        if (!emailValidation.isValid) {
          this.error = emailValidation.message;
          return { success: false, error: this.error };
        }

        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );

        // Check if email is verified
        if (!userCredential.user.emailVerified) {
          this.error = "Please verify your email before signing in.";
          await signOut(auth); // Sign out unverified user
          return {
            success: false,
            needsVerification: true,
            email: userCredential.user.email,
          };
        }

        console.log("User signed in:", userCredential.user.email);
        return { success: true, user: userCredential.user, message: "Successfully signed in!" };
      } catch (error) {
        console.error("Error signing in:", error);
        if (error.code === "auth/user-not-found") {
          this.error = "No account found with this email. Would you like to create a new account?";
        } else if (error.code === "auth/wrong-password") {
          this.error = "Incorrect password. Try again or reset your password.";
        } else if (error.code === "auth/invalid-email") {
          this.error = "Please enter a valid email address.";
        } else if (error.code === "auth/user-disabled") {
          this.error = "This account has been disabled. Contact support for help.";
        } else if (error.code === "auth/too-many-requests") {
          this.error = "Too many requests. Please try again later.";
        } else if (error.code === "auth/network-request-failed") {
          this.error = "Network error. Check your internet connection.";
        } else {
          this.error = "Failed to sign in. Please try again.";
        }
        return { success: false, error: this.error };
      } finally {
        this.loading = false;
      }
    },

    async sendEmailVerification() {
      if (!this.user) {
        this.error = "No user signed in.";
        return { success: false };
      }

      this.loading = true;
      this.error = null;
      try {
        const actionCodeSettings = {
          url: window?.location?.origin + "/verify-email",
          handleCodeInApp: false,
        };

        await sendEmailVerification(this.user, actionCodeSettings);
        console.log("Verification email sent to:", this.user.email);
        return { success: true };
      } catch (error) {
        console.error("Error sending verification email:", error);
        const formatted = handleApiError(error);
        this.error = formatted.message || "Failed to send verification email.";
        return { success: false, error: this.error, code: error.code };
      } finally {
        this.loading = false;
      }
    },

    async sendPasswordResetEmail(email) {
      this.loading = true;
      this.error = null;
      try {
        await sendPasswordResetEmail(auth, email);
        console.log("Password reset email sent to:", email);
        return { success: true };
      } catch (error) {
        console.error("Error sending password reset email:", error);
        if (error.code === "auth/user-not-found") {
          this.error =
            "No account found with this email address.";
        } else if (error.code === "auth/invalid-email") {
          this.error =
            "Please enter a valid email address.";
        } else {
          this.error =
            "Failed to send password reset email.";
        }
        return { success: false };
      } finally {
        this.loading = false;
      }
    },

    async checkEmailVerification() {
      if (!this.user) {
        return false;
      }

      try {
        await reload(this.user);
        return this.user.emailVerified;
      } catch (error) {
        console.error("Error checking email verification:", error);
        return false;
      }
    },

    // Convenience methods for AuthPage compatibility
    async register(userData) {
      const { email, password, name } = userData;
      const displayName =
        name || `${userData.firstName || ""} ${userData.lastName || ""}`.trim();
      return await this.createUserWithEmail(email, password, displayName);
    },

    async login(userData) {
      const { email, password } = userData;
      return await this.signInWithEmail(email, password);
    },

    async getCurrentUser() {
      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      });
    },
  },
});
